import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtDate, CAD } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ExternalLink, Upload, Download, UserCheck, ShieldCheck, AlertOctagon, Mail, FileCheck, Loader2, Check, DollarSign, PenTool, Trash2, AlertTriangle } from "lucide-react";
import { ExpiryBanner } from "../components/ExpiryBanner";
import DocViewerModal from "../components/DocViewerModal";
import TaskTemplatePicker from "../components/TaskTemplatePicker";
import ExpiryDocCard from "../components/ExpiryDocCard";

// Statuses requiring a modal (mandatory files/fields)
const MODAL_STATUSES = ["Submitted", "Documents Requested", "Approved", "Refused", "Withdrawn"];
const REVIEW_ACTIONS_FOR_MGR = ["Ready for Review", "Under Review", "Changes Required", "Approved for Submission"];

const stageColor = (s = "") => ({
  "New File": "bg-slate-50 text-slate-700 border-slate-200",
  "Documents Pending": "bg-amber-50 text-amber-800 border-amber-200",
  "Preparing": "bg-blue-50 text-blue-800 border-blue-200",
  "Ready for Review": "bg-indigo-50 text-indigo-800 border-indigo-200",
  "Under Review": "bg-indigo-50 text-indigo-800 border-indigo-200",
  "Changes Required": "bg-rose-50 text-rose-800 border-rose-200",
  "Approved for Submission": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Submitted": "bg-teal-50 text-teal-800 border-teal-200",
  "Documents Requested": "bg-amber-50 text-amber-800 border-amber-200",
  "Approved": "bg-green-50 text-green-800 border-green-200",
  "Refused": "bg-red-50 text-red-800 border-red-200",
  "Withdrawn": "bg-slate-50 text-slate-700 border-slate-200",
  "Closed": "bg-slate-50 text-slate-500 border-slate-200",
}[s] || "bg-slate-50 text-slate-700 border-slate-200");

export default function ImmigrationAppDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignOfficer, setAssignOfficer] = useState("");
  const [modal, setModal] = useState(null); // { newStatus, form, letterFile }
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewDeadline, setReviewDeadline] = useState("");
  const [docViewer, setDocViewer] = useState(null); // { url, title }
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const [d, m] = await Promise.all([api.get(`/immigration/applications/${id}`), api.get("/immigration/meta")]);
    setData(d.data); setMeta(m.data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!data || !meta) return <div className="text-slate-500">Loading…</div>;
  const a = data.application;
  const c = data.client;
  const perms = data.permissions;

  const patch = async (body) => { try { await api.patch(`/immigration/applications/${id}`, body); load(); } catch (e) { toast.error(e.response?.data?.detail||"Failed"); } };

  const changeStatus = async (newStatus) => {
    if (MODAL_STATUSES.includes(newStatus)) { setModal({ newStatus, form: {}, letterFile: null }); return; }
    await patch({ status: newStatus });
  };

  const uploadDoc = async (docType, fileObj) => {
    const fd = new FormData(); fd.append("file", fileObj);
    const r = await api.post(`/immigration/applications/${id}/upload?doc_type=${docType}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.id;
  };
  const genericUpload = async () => {
    const f = fileRef.current?.files?.[0]; if (!f) return toast.error("Choose a file");
    try { await uploadDoc("document", f); fileRef.current.value = ""; toast.success("Uploaded"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const download = async (f) => {
    const r = await api.get(`/immigration/files/${f.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const link = document.createElement("a"); link.href = url; link.download = f.original_filename; link.click();
    URL.revokeObjectURL(url);
  };

  const submitModal = async () => {
    if (!modal) return;
    const s = modal.newStatus;
    const body = { status: s, ...modal.form };
    const REQUIRED_LETTER = {
      "Submitted": ["submission_date", "ircc_file_number"],
      "Documents Requested": ["request_date", "response_deadline"],
      "Approved": ["approval_date"],
      "Refused": ["refusal_date", "officer_notes"],
      "Withdrawn": ["withdrawal_date", "notes"],
    }[s] || [];
    for (const k of REQUIRED_LETTER) if (!body[k]) { toast.error(`${k.replace(/_/g,' ')} required`); return; }
    // Upload letter (required except Withdrawn)
    const letterKey = { "Submitted":"submission_letter_id", "Documents Requested":"request_letter_id",
                        "Approved":"approval_letter_id", "Refused":"refusal_letter_id", "Withdrawn":"withdrawal_letter_id" }[s];
    if (letterKey && s !== "Withdrawn") {
      if (!modal.letterFile) { toast.error("Letter file required"); return; }
      if (!/\.(pdf|jpg|jpeg|png)$/i.test(modal.letterFile.name)) { toast.error("PDF/JPG/PNG only"); return; }
    }
    try {
      if (modal.letterFile && letterKey) {
        const dtype = { "Submitted":"submission_letter", "Documents Requested":"request_letter",
                        "Approved":"approval_letter", "Refused":"refusal_letter", "Withdrawn":"withdrawal_letter" }[s];
        body[letterKey] = await uploadDoc(dtype, modal.letterFile);
      }
      await api.post(`/immigration/applications/${id}/status`, body);
      toast.success(`Status → ${s}`); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doAssign = async () => {
    if (!assignOfficer) { toast.error("Pick an officer"); return; }
    try { await api.post(`/immigration/applications/${id}/assign`, { officer_id: assignOfficer }); toast.success("Assigned"); setAssignOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doReview = async (action) => {
    const body = { action };
    if (action === "changes_required") {
      if (!reviewNotes.trim()) { toast.error("Reviewer notes required"); return; }
      body.reviewer_notes = reviewNotes; body.correction_deadline = reviewDeadline || null;
    }
    try { await api.post(`/immigration/applications/${id}/review`, body); toast.success("Review recorded"); setReviewOpen(false); setReviewNotes(""); setReviewDeadline(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <Link to="/immigration/applications" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ChevronLeft className="w-4 h-4"/>Back to applications</Link>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="heading-font text-2xl font-bold text-slate-900">{c?.full_name || "—"}</h1>
              <span className="mono text-xs text-slate-500">{a.application_number}</span>
            </div>
            <div className="text-sm text-slate-600 mt-1">{a.app_type_name}</div>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <span className={`stage-badge ${stageColor(a.status)}`}>{a.status}</span>
              {a.priority && <span className="stage-badge bg-slate-50 text-slate-700 border-slate-200">Priority: {a.priority}</span>}
      {a.status_expiry_date && <span className="stage-badge bg-amber-50 text-amber-800 border-amber-200">Status Expiry: {fmtDate(a.status_expiry_date)}</span>}
              {a.assigned_officer_name ? (
                <span className="stage-badge bg-blue-50 text-blue-800 border-blue-200">Officer: {a.assigned_officer_name}</span>
              ) : (
                <span className="stage-badge bg-rose-50 text-rose-800 border-rose-200">Unassigned</span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {a.clio_matter_url && <a href={a.clio_matter_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"><ExternalLink className="w-3 h-3"/>Open Clio</a>}
            {a.drive_folder_url && <a href={a.drive_folder_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"><ExternalLink className="w-3 h-3"/>Open Drive</a>}
            {perms.assign && (
              <Button data-testid="imm-assign-btn" variant="outline" size="sm" onClick={()=>{ setAssignOfficer(a.assigned_officer_id||""); setAssignOpen(true); }}>
                <UserCheck className="w-4 h-4 mr-1"/>{a.assigned_officer_id?"Reassign":"Assign"} Officer
              </Button>
            )}
            {perms.review && REVIEW_ACTIONS_FOR_MGR.includes(a.status) && (
              <Button data-testid="imm-review-btn" size="sm" onClick={()=>setReviewOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                <ShieldCheck className="w-4 h-4 mr-1"/>Review
              </Button>
            )}
            {perms.edit && (
              <Select value={a.status} onValueChange={changeStatus}>
                <SelectTrigger data-testid="imm-status-select" className="h-9 w-56"><SelectValue/></SelectTrigger>
                <SelectContent>{meta.statuses.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {perms.edit && <TaskTemplatePicker applicationId={id} defaultAssignee={a.assigned_officer_id} meta={{ officers: meta.officers || meta.users || [] }} onCreated={load} />}
            {/* Hard-delete the entire case (admin / manager / filing manager only). Button only renders
                when backend-side perms.delete is true to avoid rendering a 403 trap for other roles. */}
            {perms.delete && (
              <Button
                data-testid="imm-delete-btn"
                variant="outline"
                size="sm"
                onClick={()=>{ setDeleteConfirm(""); setDeleteReason(""); setDeleteOpen(true); }}
                className="text-rose-700 border-rose-200 hover:bg-rose-50"
              >
                <Trash2 className="w-4 h-4 mr-1"/>Delete Case
              </Button>
            )}
          </div>
        </div>
      </div>

      <ExpiryDocCard
        applicationId={id}
        uploadPath={`/immigration/applications/${id}/upload`}
        downloadPath={`/immigration/files`}
        docType="Expiry Document"
        dateField="status_expiry_date"
        dateLabel="Status Expiry"
        expiryLabel="Current Status Expiry"
        contextChips={[
          a.app_type_name && { label: "File Type", value: a.app_type_name },
          a.ircc_file_number && { label: "IRCC #", value: a.ircc_file_number },
          a.assigned_officer_name && { label: "Officer", value: a.assigned_officer_name },
        ].filter(Boolean)}
        initialDate={a.status_expiry_date}
        initialFileId={a.expiry_doc_file_id}
        initialFileName={a.expiry_doc_file_name}
        onSaved={()=>load()}
      />

      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="workflow">Workflow</TabsTrigger>
          <TabsTrigger value="documents">Documents ({data.documents.length})</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="imm-tab-checklist">Checklist</TabsTrigger>
          <TabsTrigger value="comms" data-testid="imm-tab-comms">Communications</TabsTrigger>
          {perms.finance && <TabsTrigger value="fees" data-testid="imm-tab-fees">Fees</TabsTrigger>}
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Info label="Client Email" v={c?.email}/>
            <Info label="Client Phone" v={c?.phone}/>
            <Info label="UCI" v={c?.uci}/>
            <Info label="Application Type" v={a.app_type_name}/>
            <Info label="Assigned Officer" v={a.assigned_officer_name}/>
            <Info label="Salesperson" v={a.sales_name}/>
            <Info label="Submission Date" v={fmtDate(a.submission_date)}/>
            <Info label="IRCC File #" v={a.ircc_file_number}/>
            <Info label="Decision Date" v={fmtDate(a.decision_date)}/>
            <Info label="Processing Days" v={a.processing_days}/>
            {perms.finance && <Info label="Professional Fee" v={a.professional_fee?CAD(a.professional_fee):"—"}/>}
          </div>
        </TabsContent>

        <TabsContent value="workflow">
          {perms.edit ? (
            <div className="bg-white rounded-xl border border-slate-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Priority">
                <Select value={a.priority} onValueChange={v=>patch({priority:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{meta.priorities.map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Status Expiry Date"><Input type="date" defaultValue={a.status_expiry_date||""} onBlur={e=>e.target.value !== a.status_expiry_date && patch({status_expiry_date:e.target.value})} /></F>
              <F label="Clio Matter URL"><Input defaultValue={a.clio_matter_url||""} onBlur={e=>e.target.value !== a.clio_matter_url && patch({clio_matter_url:e.target.value})} /></F>
              <F label="Google Drive Folder URL"><Input defaultValue={a.drive_folder_url||""} onBlur={e=>e.target.value !== a.drive_folder_url && patch({drive_folder_url:e.target.value})} /></F>
              <F label="Retainer">
                <Select value={a.retainer_status||"Not Sent"} onValueChange={v=>patch({retainer_status:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{meta.retainer_statuses.map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Use of Representative">
                <Select value={a.uor_status||"Not Sent"} onValueChange={v=>patch({uor_status:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{meta.uor_statuses.map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <div className="md:col-span-2"><F label="Notes"><Textarea defaultValue={a.notes||""} onBlur={e=>e.target.value !== a.notes && patch({notes:e.target.value})} /></F></div>
              {a.reviewer_notes && (
                <div className="md:col-span-2 bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm">
                  <div className="text-xs uppercase text-rose-700 font-semibold">Reviewer Notes</div>
                  <div className="text-slate-800 mt-1 whitespace-pre-wrap">{a.reviewer_notes}</div>
                  {a.correction_deadline && <div className="text-xs text-rose-700 mt-1">Correction Deadline: {fmtDate(a.correction_deadline)}</div>}
                </div>
              )}
            </div>
          ) : <div className="bg-white p-6 rounded-xl border border-slate-200 text-sm text-slate-500">Read-only view.</div>}
        </TabsContent>

        <TabsContent value="documents">
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            {perms.edit && (
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-3">
                <input ref={fileRef} data-testid="imm-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.docx" className="text-sm" />
                <Button size="sm" onClick={genericUpload}><Upload className="w-4 h-4 mr-1"/>Upload Document</Button>
              </div>
            )}
            <div className="divide-y divide-slate-100">
              {data.documents.length===0 && <div className="p-4 text-sm text-slate-500">No documents uploaded.</div>}
              {data.documents.map(f=>(
                <div key={f.id} className="flex items-center justify-between py-3">
                  <div><div className="text-sm font-medium">{f.original_filename}</div><div className="text-xs text-slate-500">{f.doc_type} · {fmtDate(f.created_at)}</div></div>
                  <Button variant="outline" size="sm" onClick={()=>download(f)}><Download className="w-4 h-4 mr-1"/>Download</Button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="checklist">
          <ChecklistPanel appId={id} canEdit={perms.edit} />
        </TabsContent>

        <TabsContent value="comms">
          <CommsPanel appId={id} clientEmail={c?.email} welcomeAt={a.welcome_email_sent_at} nextStepsAt={a.next_steps_email_sent_at} currentStatus={a.status} onDone={load} canEdit={perms.edit} />
        </TabsContent>

        {perms.finance && (
          <TabsContent value="fees">
            <FeesPanel appId={id} canEdit={perms.edit} />
          </TabsContent>
        )}

        <TabsContent value="activity">
          <div className="flex items-center justify-end mb-3">
            <Button variant="outline" size="sm" data-testid="imm-timeline-export" onClick={async ()=>{
              try {
                const r = await api.get(`/immigration/applications/${id}/timeline.pdf`, { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                const link = document.createElement("a"); link.href = url; link.download = `Timeline-${a.application_number || "case"}.pdf`; link.click();
                URL.revokeObjectURL(url);
              } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
            }}>
              <Download className="w-4 h-4 mr-1"/>Export Timeline PDF
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {data.activity.length === 0 && <div className="p-4 text-sm text-slate-500">No activity yet.</div>}
            {data.activity.map(x=>(
              <div key={x.id} className="p-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-900">{x.actor_name || "System"}</span>
                  <span className="text-xs text-slate-500">· {x.action}</span>
                  <span className="text-xs text-slate-400 ml-auto">{fmtDate(x.created_at)}</span>
                </div>
                {(x.old_value != null || x.new_value != null) && (
                  <div className="text-xs text-slate-600 mt-1">
                    {x.old_value != null && <><span className="text-slate-400">from </span><span className="mono">{String(x.old_value)}</span> </>}
                    {x.new_value != null && <><span className="text-slate-400">→ </span><span className="mono">{String(x.new_value)}</span></>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Assign Officer Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{a.assigned_officer_id?"Reassign":"Assign"} Filing Officer</DialogTitle></DialogHeader>
          <Select value={assignOfficer} onValueChange={setAssignOfficer}>
            <SelectTrigger data-testid="imm-assign-select"><SelectValue placeholder="Pick officer"/></SelectTrigger>
            <SelectContent>{meta.officers.map(o=><SelectItem key={o.user_id} value={o.user_id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setAssignOpen(false)}>Cancel</Button>
            <Button data-testid="imm-assign-save" onClick={doAssign} className="bg-orange-500 hover:bg-orange-600 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Review Application</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {a.status !== "Under Review" && (
              <Button variant="outline" onClick={()=>doReview("start_review")}>Mark as Under Review</Button>
            )}
            <div>
              <Label className="text-xs text-slate-600">Reviewer Notes (required if changes)</Label>
              <Textarea data-testid="imm-review-notes" value={reviewNotes} onChange={e=>setReviewNotes(e.target.value)} rows={4} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Correction Deadline (optional)</Label>
              <Input type="date" value={reviewDeadline} onChange={e=>setReviewDeadline(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setReviewOpen(false)}>Close</Button>
            <Button data-testid="imm-review-changes" variant="outline" onClick={()=>doReview("changes_required")} className="text-rose-600 border-rose-200 hover:bg-rose-50"><AlertOctagon className="w-4 h-4 mr-1"/>Request Changes</Button>
            <Button data-testid="imm-review-approve" onClick={()=>doReview("approve_for_submission")} className="bg-emerald-600 hover:bg-emerald-700 text-white">Approve for Submission</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Modal */}
      <Dialog open={!!modal} onOpenChange={o=>!o && setModal(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle>{modal?.newStatus}</DialogTitle></DialogHeader>
          {modal && <StatusModalBody modal={modal} setModal={setModal} />}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setModal(null)}>Cancel</Button>
            <Button data-testid="imm-status-save" onClick={submitModal} className="bg-orange-500 hover:bg-orange-600 text-white">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocViewerModal open={!!docViewer} onOpenChange={(v)=>{ if (!v) setDocViewer(null); }} url={docViewer?.url} title={docViewer?.title} />

      {/* Delete-case confirmation dialog. Cascades through every linked collection
          (tasks, files, retainer, invoices, payments, emails, audit) on the server. */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="heading-font flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5"/>Delete this case?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-xs text-rose-900">
              This permanently removes <span className="font-semibold">{a.application_number}</span>
              {c?.full_name ? <> for <span className="font-semibold">{c.full_name}</span></> : null} and every linked
              task, file, retainer, invoice, payment and email. This cannot be undone.
            </div>
            <div>
              <Label className="text-xs text-slate-600">Reason (optional, saved to audit log)</Label>
              <Textarea rows={2} value={deleteReason} onChange={e=>setDeleteReason(e.target.value)} data-testid="imm-delete-reason"/>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Type <span className="mono font-bold">DELETE</span> to confirm</Label>
              <Input value={deleteConfirm} onChange={e=>setDeleteConfirm(e.target.value)} placeholder="DELETE" data-testid="imm-delete-confirm"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
            <Button
              data-testid="imm-delete-submit"
              onClick={async ()=>{
                if (deleteConfirm.trim().toUpperCase() !== "DELETE") { toast.error('Type DELETE to confirm'); return; }
                setDeleting(true);
                try {
                  const params = new URLSearchParams({ confirm: "DELETE" });
                  if (deleteReason.trim()) params.set("reason", deleteReason.trim());
                  const r = await api.delete(`/immigration/applications/${id}?${params.toString()}`);
                  const d = r.data?.deleted || {};
                  toast.success(`Case deleted · removed ${(d.immigration_tasks||0)} tasks, ${(d.immigration_files||0)} files, ${(d.immigration_invoices||0)+(d.immigration_payments||0)} finance rows`);
                  nav("/immigration/applications");
                } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
                finally { setDeleting(false); }
              }}
              disabled={deleting || deleteConfirm.trim().toUpperCase() !== "DELETE"}
              className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50"
            >
              {deleting ? "Deleting…" : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, v }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="text-xs uppercase text-slate-500">{label}</div>
    <div className="text-sm text-slate-900 mt-1">{v || "—"}</div>
  </div>
);

function StatusModalBody({ modal, setModal }) {
  const s = modal.newStatus;
  const setF = (k, v) => setModal(m => ({ ...m, form: { ...m.form, [k]: v } }));
  const setFile = (f) => setModal(m => ({ ...m, letterFile: f }));
  return (
    <div className="space-y-3">
      {s === "Submitted" && (<>
        <F label="Submission Date *"><Input data-testid="imm-submission-date" type="date" value={modal.form.submission_date||""} onChange={e=>setF("submission_date", e.target.value)} /></F>
        <F label="IRCC Application/File Number *"><Input data-testid="imm-ircc" value={modal.form.ircc_file_number||""} onChange={e=>setF("ircc_file_number", e.target.value)} /></F>
        <F label="Submission Confirmation Letter (PDF/JPG/PNG) *"><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e=>setFile(e.target.files?.[0])} /></F>
        <F label="Officer Notes"><Textarea value={modal.form.officer_notes||""} onChange={e=>setF("officer_notes", e.target.value)} /></F>
      </>)}
      {s === "Documents Requested" && (<>
        <F label="Request Date *"><Input type="date" value={modal.form.request_date||""} onChange={e=>setF("request_date", e.target.value)} /></F>
        <F label="Response Deadline *"><Input type="date" value={modal.form.response_deadline||""} onChange={e=>setF("response_deadline", e.target.value)} /></F>
        <F label="IRCC Request Letter *"><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e=>setFile(e.target.files?.[0])} /></F>
        <F label="Notes"><Textarea value={modal.form.officer_notes||""} onChange={e=>setF("officer_notes", e.target.value)} /></F>
      </>)}
      {s === "Approved" && (<>
        <F label="Approval Date *"><Input type="date" value={modal.form.approval_date||""} onChange={e=>setF("approval_date", e.target.value)} /></F>
        <F label="Approval Letter *"><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e=>setFile(e.target.files?.[0])} /></F>
        <F label="Notes"><Textarea value={modal.form.officer_notes||""} onChange={e=>setF("officer_notes", e.target.value)} /></F>
      </>)}
      {s === "Refused" && (<>
        <F label="Refusal Date *"><Input type="date" value={modal.form.refusal_date||""} onChange={e=>setF("refusal_date", e.target.value)} /></F>
        <F label="Refusal Letter *"><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e=>setFile(e.target.files?.[0])} /></F>
        <F label="Officer Notes *"><Textarea value={modal.form.officer_notes||""} onChange={e=>setF("officer_notes", e.target.value)} /></F>
      </>)}
      {s === "Withdrawn" && (<>
        <F label="Withdrawal Date *"><Input type="date" value={modal.form.withdrawal_date||""} onChange={e=>setF("withdrawal_date", e.target.value)} /></F>
        <F label="Reason / Notes *"><Textarea value={modal.form.notes||""} onChange={e=>setF("notes", e.target.value)} /></F>
        <F label="Supporting Document (optional)"><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e=>setFile(e.target.files?.[0])} /></F>
      </>)}
    </div>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>
);

function ChecklistPanel({ appId, canEdit }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailOverride, setEmailOverride] = useState("");

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/immigration/applications/${appId}/checklist`); setState(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load checklist"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [appId]);

  const toggle = async (key, received) => {
    try {
      await api.post(`/immigration/applications/${appId}/checklist/toggle`, { key, received });
      setState(s => ({ ...s, items: s.items.map(it => it.key === key ? { ...it, received } : it) }));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/immigration/applications/${appId}/checklist.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Checklist-${state?.application_number || "case"}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate PDF"); }
    finally { setDownloading(false); }
  };

  const emailClient = async () => {
    setEmailing(true);
    try {
      const body = emailOverride ? { to: emailOverride } : {};
      const r = await api.post(`/immigration/applications/${appId}/checklist/email`, body);
      if (r.data.ok) toast.success(`Checklist emailed to ${r.data.to}`);
      else toast.error("Email send failed — check email configuration.");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    finally { setEmailing(false); }
  };

  if (loading && !state) return <div className="bg-white p-6 rounded-xl border border-slate-200 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading checklist…</div>;
  if (!state) return null;

  const totals = state.items.reduce((a, it) => ({ total: a.total + 1, done: a.done + (it.received ? 1 : 0), mand: a.mand + (it.mandatory ? 1 : 0) }), { total: 0, done: 0, mand: 0 });
  const defaultEmail = state.client_email || "";

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase text-slate-500">Document Checklist</div>
          <div className="heading-font text-lg font-semibold">{state.app_type_name}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {state.has_template
              ? <>{totals.done} of {totals.total} received · {totals.mand} mandatory</>
              : <span className="text-amber-700">No template configured yet for this application type.</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading} data-testid="imm-checklist-pdf">
            {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Download className="w-4 h-4 mr-1"/>}Download PDF
          </Button>
          {canEdit && (
            <>
              <Input
                className="h-9 w-56"
                placeholder={defaultEmail || "client email"}
                value={emailOverride}
                onChange={e => setEmailOverride(e.target.value)}
                data-testid="imm-checklist-email-to"
              />
              <Button size="sm" onClick={emailClient} disabled={emailing || (!defaultEmail && !emailOverride)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="imm-checklist-email">
                {emailing ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Mail className="w-4 h-4 mr-1"/>}Email Client
              </Button>
            </>
          )}
        </div>
      </div>

      {state.items.length === 0 ? (
        <div className="rounded-lg bg-slate-50 border border-dashed border-slate-300 p-6 text-sm text-slate-500 text-center">
          <FileCheck className="w-6 h-6 mx-auto text-slate-400 mb-2"/>
          No items on this checklist yet. An admin can add them under <Link className="text-orange-600 hover:underline" to="/immigration/checklist-templates">Checklist Templates</Link>.
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
          {state.items.map((it, idx) => (
            <label key={it.key} className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer" data-testid={`imm-checklist-row-${idx}`}>
              <input
                type="checkbox"
                checked={!!it.received}
                disabled={!canEdit}
                onChange={e => toggle(it.key, e.target.checked)}
                className="w-4 h-4 accent-orange-500"
                data-testid={`imm-checklist-check-${idx}`}
              />
              <div className="text-xs text-slate-400 w-5 text-right">{idx + 1}.</div>
              <div className="flex-1 text-sm text-slate-900">{it.label}</div>
              {it.mandatory ? (
                <span className="stage-badge bg-rose-50 text-rose-700 border-rose-200">Mandatory</span>
              ) : (
                <span className="stage-badge bg-slate-50 text-slate-600 border-slate-200">Optional</span>
              )}
              <span className={`stage-badge ${it.received ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                {it.received ? "Received" : "Pending"}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function CommsPanel({ appId, clientEmail, welcomeAt, nextStepsAt, currentStatus, onDone, canEdit }) {
  const [sending, setSending] = useState(null);
  const [override, setOverride] = useState("");
  const send = async (kind) => {
    setSending(kind);
    try {
      const body = override ? { to: override } : {};
      const r = await api.post(`/immigration/applications/${appId}/email/${kind}`, body);
      if (r.data.ok) toast.success(`${kind === "welcome" ? "Welcome" : "Next-steps"} email sent to ${r.data.to}`);
      else toast.error("Email send failed — check email configuration.");
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSending(null); }
  };
  const Btn = ({ kind, title, subtitle, sentAt }) => {
    const alreadySent = !!sentAt;
    return (
      <div className="rounded-xl border border-slate-200 p-4 flex items-start gap-4">
        <div className="w-9 h-9 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
          <Mail className="w-5 h-5"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
          {alreadySent && <div className="text-[11px] text-emerald-700 mt-1">Sent on {fmtDate(sentAt)}</div>}
        </div>
        {alreadySent ? (
          <Button
            size="sm"
            disabled
            className="bg-emerald-600 text-white opacity-100 cursor-not-allowed"
            data-testid={`imm-sent-${kind}`}
            title="This email has already been sent to the client"
          >
            <Check className="w-4 h-4 mr-1"/>Sent
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={!canEdit || sending === kind || (!clientEmail && !override)}
            onClick={() => send(kind)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid={`imm-send-${kind}`}
          >
            {sending === kind ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Mail className="w-4 h-4 mr-1"/>}Send
          </Button>
        )}
      </div>
    );
  };
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 flex-wrap">
        <div className="text-xs uppercase text-slate-500">Send to</div>
        <div className="mono text-sm text-slate-700">{clientEmail || <span className="text-amber-700">no email on file</span>}</div>
        <div className="flex-1"/>
        <Input placeholder="Override recipient (optional)" value={override} onChange={e => setOverride(e.target.value)} className="w-72 h-9" data-testid="imm-comms-override"/>
      </div>
      <Btn kind="welcome" title="Welcome Email" subtitle="Introduces the firm, confirms case ID, lists what happens next." sentAt={welcomeAt}/>
      <Btn kind="next-steps" title={`Next-Steps Email · ${currentStatus}`} subtitle="Status-aware update describing exactly what the client should expect right now." sentAt={nextStepsAt}/>
      <p className="text-xs text-slate-500">Both templates are rendered server-side with the firm's letterhead and the client's name/case ID auto-filled. No HTML editing from the browser.</p>
    </div>
  );
}

function FeesPanel({ appId, canEdit }) {
  const [fee, setFee] = useState(null);
  const [draftPf, setDraftPf] = useState("");
  const [draftPaid, setDraftPaid] = useState("");
  const [discOpen, setDiscOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [discAmt, setDiscAmt] = useState("");
  const [discReason, setDiscReason] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    const r = await api.get(`/immigration/applications/${appId}/fee`);
    setFee(r.data);
    setDraftPf(String(r.data.professional_fee || ""));
    setDraftPaid(String(r.data.amount_paid || ""));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [appId]);

  const saveFee = async () => {
    try {
      await api.patch(`/immigration/applications/${appId}/fee`, {
        professional_fee: Number(draftPf) || 0,
        amount_paid: Number(draftPaid) || 0,
      });
      toast.success("Fee updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const requestDiscount = async () => {
    if (!discReason.trim() || !Number(discAmt)) return toast.error("Amount and reason required");
    setBusy(true);
    try {
      const r = await api.post(`/immigration/applications/${appId}/fee/request-discount`, {
        discount_amount: Number(discAmt), discount_reason: discReason.trim(),
      });
      toast.success(r.data.status === "approved" ? "Discount applied" : "Discount sent for manager approval");
      setDiscOpen(false); setDiscAmt(""); setDiscReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try { await api.post(`/immigration/applications/${appId}/fee/approve-discount`); toast.success("Discount approved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    if (!rejectReason.trim()) return toast.error("Reason required");
    setBusy(true);
    try { await api.post(`/immigration/applications/${appId}/fee/reject-discount`, { reason: rejectReason.trim() }); toast.success("Discount rejected"); setRejectOpen(false); setRejectReason(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const downloadInvoice = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/immigration/applications/${appId}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a"); link.href = url; link.download = `Invoice-${fee.application_number || "case"}.pdf`; link.click();
      URL.revokeObjectURL(url);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate invoice"); }
    finally { setDownloading(false); }
  };

  if (!fee) return <div className="bg-white p-6 rounded-xl border border-slate-200 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading fees…</div>;

  const statusBadge = {
    none: null,
    pending: <span className="stage-badge bg-amber-50 text-amber-800 border-amber-200">Pending Approval</span>,
    approved: <span className="stage-badge bg-emerald-50 text-emerald-700 border-emerald-200">Discount Approved</span>,
    rejected: <span className="stage-badge bg-rose-50 text-rose-700 border-rose-200">Discount Rejected</span>,
  }[fee.discount_status || "none"];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <div className="text-xs uppercase text-slate-500">Master Base Fee</div>
          <div className="mono text-lg font-semibold mt-1">{fee.master_base_fee != null ? CAD(fee.master_base_fee) : <span className="text-slate-400 text-sm font-normal">Not set</span>}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">Tax rate: {(fee.master_tax_rate * 100).toFixed(1)}%</div>
        </div>
        <div>
          <Label className="text-xs text-slate-600">This Case · Fee (CAD)</Label>
          <Input type="number" min="0" step="0.01" value={draftPf} onChange={e => setDraftPf(e.target.value)} disabled={!canEdit} data-testid="imm-fee-pf"/>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Amount Paid (CAD)</Label>
          <Input type="number" min="0" step="0.01" value={draftPaid} onChange={e => setDraftPaid(e.target.value)} disabled={!canEdit} data-testid="imm-fee-paid"/>
        </div>
        <div className="flex items-end">
          {canEdit && <Button onClick={saveFee} className="bg-slate-900 hover:bg-slate-700 text-white w-full" data-testid="imm-fee-save-case">Save</Button>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase text-slate-500">Discount</div>
            <div className="text-sm text-slate-900 mt-1">
              {fee.discount_amount > 0 ? (
                <>
                  <span className="mono font-semibold">{CAD(fee.discount_amount)}</span>
                  <span className="text-slate-500"> — {fee.discount_reason}</span>
                </>
              ) : <span className="text-slate-500">No discount applied</span>}
            </div>
            <div className="mt-2 flex items-center gap-2">
              {statusBadge}
              {fee.discount_status === "pending" && fee.discount_requested_by_name && (
                <span className="text-[11px] text-slate-500">Requested by {fee.discount_requested_by_name}</span>
              )}
              {fee.discount_status === "approved" && fee.discount_approved_by_name && (
                <span className="text-[11px] text-slate-500">Approved by {fee.discount_approved_by_name}</span>
              )}
              {fee.discount_status === "rejected" && fee.discount_rejection_reason && (
                <span className="text-[11px] text-rose-600">{fee.discount_rejection_reason}</span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {canEdit && fee.discount_status !== "pending" && (
              <Button variant="outline" onClick={() => setDiscOpen(true)} data-testid="imm-fee-request-discount">
                {fee.discount_status === "approved" ? "Adjust Discount" : "Request Discount"}
              </Button>
            )}
            {fee.discount_status === "pending" && fee.can_approve_discount && (
              <>
                <Button onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="imm-fee-approve">Approve</Button>
                <Button variant="outline" onClick={() => setRejectOpen(true)} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="imm-fee-reject">Reject</Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase text-slate-500">Billing Summary</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2 text-sm">
              <div className="text-slate-500">Fee</div><div className="mono text-right">{CAD(fee.professional_fee)}</div>
              {fee.discount_status === "approved" && fee.discount_amount > 0 && (<>
                <div className="text-slate-500">Discount</div><div className="mono text-right text-rose-700">-{CAD(fee.discount_amount)}</div>
              </>)}
              <div className="text-slate-500">Subtotal</div><div className="mono text-right">{CAD(fee.net_fee)}</div>
              {fee.master_tax_rate > 0 && (<>
                <div className="text-slate-500">Tax ({(fee.master_tax_rate*100).toFixed(1)}%)</div><div className="mono text-right">{CAD(fee.tax_amount)}</div>
              </>)}
              <div className="text-slate-900 font-semibold">Total Due</div><div className="mono text-right font-semibold">{CAD(fee.total_due)}</div>
              <div className="text-slate-500">Paid</div><div className="mono text-right">-{CAD(fee.amount_paid)}</div>
              <div className="text-slate-900 font-semibold">Balance Due</div><div className="mono text-right font-semibold">{CAD(fee.balance_due)}</div>
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <Button onClick={downloadInvoice} disabled={downloading} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="imm-invoice-pdf">
              {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Download className="w-4 h-4 mr-1"/>}
              {fee.invoice_number ? "Download Invoice PDF" : "Generate Invoice PDF"}
            </Button>
            <InvoiceEmailBtn appId={appId} canEdit={canEdit} fee={fee} onDone={load}/>
            {fee.invoice_number && (
              <div className="text-xs text-slate-500">
                <span className="mono">{fee.invoice_number}</span>
                {fee.invoice_issued_at && <> · issued {fmtDate(fee.invoice_issued_at)}</>}
              </div>
            )}
          </div>
        </div>
      </div>

      <PaymentsBlock appId={appId} canEdit={canEdit} onPaid={load}/>
      <RetainerBlock appId={appId} canEdit={canEdit}/>

      {/* Request/Adjust Discount Dialog */}
      <Dialog open={discOpen} onOpenChange={setDiscOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Request Discount</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Discount Amount (CAD)</Label>
              <Input type="number" min="0" step="0.01" value={discAmt} onChange={e => setDiscAmt(e.target.value)} data-testid="imm-fee-disc-amt"/>
              <div className="text-xs text-slate-400 mt-1">Must be less than or equal to CA${(fee.professional_fee || 0).toFixed(2)}</div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Reason / Justification</Label>
              <Textarea value={discReason} onChange={e => setDiscReason(e.target.value)} rows={3} data-testid="imm-fee-disc-reason"/>
            </div>
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              {fee.can_approve_discount
                ? "You can approve your own request — it will apply immediately."
                : "Managers will review and approve/reject this request."}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscOpen(false)}>Cancel</Button>
            <Button onClick={requestDiscount} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="imm-fee-disc-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Reject Discount</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-slate-600">Reason</Label>
            <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} data-testid="imm-fee-reject-reason"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button onClick={reject} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="imm-fee-reject-submit">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function InvoiceEmailBtn({ appId, canEdit, fee, onDone }) {
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState(fee?.invoice_emailed_at || null);
  useEffect(() => { setSentAt(fee?.invoice_emailed_at || null); }, [fee?.invoice_emailed_at]);
  const alreadySent = !!sentAt;
  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/immigration/applications/${appId}/invoice/email`, {});
      if (r.data.ok) { toast.success(`Invoice emailed to ${r.data.to}`); setSentAt(new Date().toISOString()); onDone && onDone(); }
      else toast.error("Email send failed — check email configuration.");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };
  if (alreadySent) return (
    <Button size="sm" disabled className="bg-emerald-600 text-white opacity-100 cursor-not-allowed" data-testid="imm-invoice-sent" title="This invoice has already been emailed">
      <Check className="w-4 h-4 mr-1"/>Invoice Emailed
    </Button>
  );
  return (
    <Button size="sm" onClick={send} disabled={!canEdit || sending} variant="outline" data-testid="imm-invoice-email">
      {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Mail className="w-4 h-4 mr-1"/>}Email Invoice
    </Button>
  );
}

function PaymentsBlock({ appId, canEdit, onPaid }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("manual_bank");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { try { const r = await api.get(`/immigration/applications/${appId}/payments`); setRows(r.data || []); } catch {} };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [appId]);
  const submit = async () => {
    if (!Number(amount)) return toast.error("Amount required");
    setBusy(true);
    try {
      const fd = new FormData();
      if (file) fd.append("screenshot", file);
      const qs = new URLSearchParams({ amount, method, reference, notes }).toString();
      await api.post(`/immigration/applications/${appId}/payments?${qs}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Payment recorded"); setOpen(false); setAmount(""); setReference(""); setNotes(""); setFile(null);
      await load(); onPaid && onPaid();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const METHOD_LABEL = { manual_bank: "Bank Transfer", manual_cash: "Cash", manual_etransfer: "e-Transfer", stripe: "Stripe", other: "Other" };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-xs uppercase text-slate-500">Payments</div>
          <div className="text-sm text-slate-500 mt-0.5">{rows.length} payment{rows.length === 1 ? "" : "s"} recorded</div>
        </div>
        {canEdit && <Button size="sm" onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-700 text-white" data-testid="imm-payment-log"><DollarSign className="w-4 h-4 mr-1"/>Log Payment</Button>}
      </div>
      <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
        {rows.length === 0 && <div className="p-4 text-sm text-slate-500">No payments yet.</div>}
        {rows.map(p => (
          <div key={p.id} className="p-3 flex items-center gap-4 text-sm">
            <div className="w-24 text-slate-500 text-xs">{fmtDate(p.received_at)}</div>
            <div className="mono font-semibold">{CAD(p.amount)}</div>
            <div className="text-xs text-slate-500">{METHOD_LABEL[p.method] || p.method}{p.reference ? ` · ${p.reference}` : ""}</div>
            <div className="flex-1 text-xs text-slate-400 truncate">{p.notes || ""}</div>
            {p.screenshot_file_id && (
              <Button variant="ghost" size="sm" onClick={async () => {
                try { const r = await api.get(`/immigration/files/${p.screenshot_file_id}/download`, { responseType: "blob" });
                  const url = URL.createObjectURL(r.data); window.open(url, "_blank"); } catch {}
              }}><Download className="w-3 h-3 mr-1"/>Screenshot</Button>
            )}
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Log Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-600">Amount (CAD)</Label><Input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} data-testid="pay-amount"/></div>
              <div><Label className="text-xs text-slate-600">Method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="pay-method"><SelectValue/></SelectTrigger>
                  <SelectContent>{Object.entries(METHOD_LABEL).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs text-slate-600">Reference (txn id, cheque #, etc.)</Label><Input value={reference} onChange={e => setReference(e.target.value)}/></div>
            <div><Label className="text-xs text-slate-600">Notes</Label><Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}/></div>
            <div><Label className="text-xs text-slate-600">Screenshot / Receipt (optional)</Label>
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={e => setFile(e.target.files?.[0] || null)} className="block w-full text-sm" data-testid="pay-file"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="pay-submit">{busy ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RetainerBlock({ appId, canEdit }) {
  const [state, setState] = useState(null);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const [r, e] = await Promise.all([
        api.get(`/immigration/applications/${appId}/retainer`),
        api.get(`/immigration/applications/${appId}/retainer/events`),
      ]);
      setState(r.data); setEvents(e.data || []);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [appId]);
  const issue = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/immigration/applications/${appId}/retainer/issue`, {});
      if (r.data.email_ok) toast.success(`Retainer sent to ${r.data.emailed_to}`);
      else toast.success("Retainer link ready — email to client manually.");
      navigator.clipboard?.writeText(r.data.sign_url).catch(() => {});
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const downloadSigned = async () => {
    try {
      const r = await api.get(`/immigration/applications/${appId}/retainer/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a"); link.href = url; link.download = `Retainer-${appId}.pdf`; link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const label = state?.exists ? (state.status === "signed" ? "Signed" : "Awaiting Signature") : "Not Issued";
  const badgeCls = state?.status === "signed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                   state?.exists ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200";
  const evLabel = (t) => (t || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase text-slate-500">Retainer Agreement (E-Sign)</div>
          <div className="text-sm text-slate-500 mt-0.5">
            {state?.status === "signed"
              ? <>Signed on {state.client_signed_at?.slice(0,10)} · auto counter-signed by {state.firm_rcic_name || "firm"}.</>
              : state?.exists ? <>Signing link active — share it with the client if the email didn't arrive.</>
                : <>Generate a retainer PDF and send a secure signing link to the client.</>}
          </div>
          <span className={`stage-badge mt-2 inline-flex ${badgeCls}`}>{label}</span>
        </div>
        <div className="flex gap-2">
          {state?.status !== "signed" && canEdit && (
            <Button onClick={issue} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="imm-retainer-issue">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <PenTool className="w-4 h-4 mr-1"/>}
              {state?.exists ? "Resend Link" : "Issue Retainer"}
            </Button>
          )}
          {state?.exists && (
            <Button variant="outline" size="sm" onClick={downloadSigned}><Download className="w-4 h-4 mr-1"/>{state.status === "signed" ? "Signed PDF" : "Preview PDF"}</Button>
          )}
        </div>
      </div>
      {events.length > 0 && (
        <div className="border-t border-slate-100 pt-4">
          <div className="text-xs uppercase text-slate-500 mb-2">Signing Activity &amp; Audit Trail ({events.length})</div>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase">
                <tr>
                  <th className="text-left px-3 py-2">When (UTC)</th>
                  <th className="text-left px-3 py-2">Event</th>
                  <th className="text-left px-3 py-2">Actor</th>
                  <th className="text-left px-3 py-2">IP · Device</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {events.map(e => (
                  <tr key={e.id} data-testid={`retainer-evt-${e.type}`}>
                    <td className="px-3 py-2 mono text-slate-700">{(e.created_at || "").slice(0,19).replace("T"," ")}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">{evLabel(e.type)}</td>
                    <td className="px-3 py-2 text-slate-700">{e.actor_name || e.actor}</td>
                    <td className="px-3 py-2 text-slate-500">
                      {e.ip ? <span className="mono">{e.ip}</span> : "—"}
                      {e.user_agent && <span className="block text-[10px] text-slate-400 truncate max-w-[220px]" title={e.user_agent}>{e.user_agent}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Every event — issuance, email send, client view, signature, firm counter-signature — is immutably logged with timestamp,
            IP address, and user-agent. The resulting PDF carries a SHA-256 fingerprint for integrity verification.
          </p>
        </div>
      )}
    </div>
  );
}

